import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { axiosInstance } from "../../axios/axiosInstance";
import { BreadCrumb } from "../../Layout/Common/breadCrumb";
import { isValidArray, normalizePath, normalizeUrlPath } from "../../Layout/Common/common";
import { ImageWithDefault } from "../../Layout/Common/imageWithDefault";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { BrandSkeleton } from "../../Skeleton/brandSkeleton";
import { Link, useParams } from "react-router-dom";
import { PAGES } from "../../routeConfig";
import { GlobalContext } from "../../Context/GlobalContext";
import { BrandPageMain } from "./brandPageMain";
import { usePageTitle } from "../../utils/pageTitle";

// Hoisted so the brand-logo <img> style isn't a fresh object on every render.
const BRAND_LOGO_STYLE = { width: "180px", height: "100px", objectFit: "contain" };

export const ManufacturerPage = () => {
  const [Breadcrumb, setBreadcrumb] = useState([]);
  const [manufacturer, setManufacturer] = useState(null);
  const [filterAlpha, setFilterAlpha] = useState(null);
  const [selectedAlpha, setSelectedAlpha] = useState("ALL");
  const { setBrandList, brandList } = useContext(GlobalContext);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const objectRef = useRef();
  const { brandName } = useParams();

  // On /brand the router's "Brands" title stands; once a brand is opened the
  // tab shows that brand's name.
  usePageTitle(selectedBrand?.name);

  useEffect(() => {
    setBreadcrumb([{ name: `Brand`, url: PAGES.BRAND }]);
    getManufacturerList();
  }, []);

  useEffect(() => {
    if (brandName && isValidArray(brandList)) {
      const brand = selectedBrand ?? brandList?.find((b) => normalizeUrlPath(b.name) == brandName);
      handleBrandSelect(brand);
    } else {
      setSelectedBrand(null);
      setBreadcrumb([{ name: `Brand`, url: null }]);
    }
  }, [brandName, brandList]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setBreadcrumb([
      { name: `Brand`, url: PAGES.BRAND },
      { name: brand.name, url: null },
    ]);
  };

  const getManufacturerList = async () => {
    const response = await axiosInstance.get("GetStaticData/GetManufacturerList");
    if (isValidArray(response.data)) {
      let sortedItem = response.data.sort((a, b) => a.sortId - b.sortId);
      objectRef.current = sortedItem;
      let nameList = response.data.map((item) => item.name.charAt(0).toUpperCase());
      const uniqueNames = [...new Set(nameList)];
      setFilterAlpha(uniqueNames.sort((a, b) => a.localeCompare(b)));
      setManufacturer(sortedItem);
      setBrandList(sortedItem);
      setSelectedAlpha("ALL");
    }
  };

  const handleFilterAlpha = (alpha) => {
    setSelectedAlpha(alpha);
    if (alpha == "ALL") setManufacturer(objectRef.current);
    else {
      let item = objectRef.current.filter((item) => String(item.name).toUpperCase().startsWith(alpha));
      setManufacturer(item);
    }
  };

  // Debounced so the grid re-filters (and its fade transition) at most every 300ms while typing,
  // instead of on every keystroke. Stable reference via useCallback so the debounce timer persists.
  const debouncedSearch = useCallback(
    debounce((txt) => {
      try {
        if (!objectRef.current) return;
        const query = String(txt).toUpperCase();
        const item = objectRef.current.filter((item) => String(item.name).toUpperCase().includes(query));
        setManufacturer(item);
      } catch (err) {
        console.error("e", err);
      }
    }, 300),
    [],
  );

  const handleSearch = (e) => {
    debouncedSearch(e?.target?.value ?? "");
  };

  // Drop any pending debounced filter when the page unmounts.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <Fragment>
      <BreadCrumb data={Breadcrumb} />
      {selectedBrand ? (
        <BrandPageMain brand={selectedBrand} />
      ) : (
        <section className="brand-list-section">
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-sm-6 col-lg-6 shop-brand-title">
                <h1>Shop By Brand</h1>
              </div>
              <div className="col-sm-6 col-lg-6">
                <div className="shop-brand-search position-relative float-end">
                  <input
                    className="form-control me-2"
                    type="text"
                    name="searchbox"
                    placeholder="Search here"
                    aria-label="Search"
                    onChange={handleSearch}
                    autoComplete="off"
                  />
                  <span className="wes-search" />
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-lg-12">
                <div className="brands-list">
                  <ul>
                    <li>
                      <a
                        role="button"
                        onClick={() => handleFilterAlpha("ALL")}
                        className={`${selectedAlpha == "ALL" && "active"}`}
                      >
                        All
                      </a>
                    </li>
                    {filterAlpha &&
                      filterAlpha.map((item) => (
                        <li className="border-link" key={item}>
                          <a
                            role="button"
                            className={`${selectedAlpha == item && "active"}`}
                            onClick={() => handleFilterAlpha(item)}
                          >
                            {item}
                          </a>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
            <TransitionGroup className={"row row-equal-coloumn results-min-height"}>
              {isValidArray(manufacturer) ? (
                manufacturer.map((row, index) => (
                  <CSSTransition key={row.id} timeout={500} classNames="fade">
                    <div className="col-4 col-sm-3 col-md-3 col-lg-3 mt-3 mt-lg-4">
                      <div className="branding-list">
                        <Link
                          to={PAGES.BRAND + "/" + normalizeUrlPath(row.name)}
                          onClick={() => setSelectedBrand(row)}
                          className="brand-list-effect"
                        >
                          <ImageWithDefault src={row.imageUrl} style={BRAND_LOGO_STYLE} />
                          <span>
                            {row.name}
                            <span></span>
                          </span>
                        </Link>
                      </div>
                    </div>
                  </CSSTransition>
                ))
              ) : objectRef?.current ? (
                <CSSTransition key={-1} timeout={500} classNames="fade">
                  <div className="container d-flex mt-12 mt-lg-4">
                    <div style={{ margin: "auto" }}>No Result Found</div>
                  </div>
                </CSSTransition>
              ) : (
                <CSSTransition key={0} timeout={500} classNames="fade">
                  <BrandSkeleton count={4} />
                </CSSTransition>
              )}
            </TransitionGroup>
          </div>
        </section>
      )}
    </Fragment>
  );
};
